import {
  Either, Maybe,
} from 'monet';

/**
 * @description Базовое уведомление.
 * @param {string}  body      Текст уведомления.
 * @param {string}  dateTime  Дата и время отправки уведомления.
 * @param {number}  id        Идентификатор уведомления.
 * @param {boolean} isViewed  Просмотрено.
 * @param {string}  title     Заголовок.
 * @param {string}  type      Тип уведомления.
 * @constructor
 */
function BaseNotification(
  body,
  dateTime,
  id,
  isViewed,
  title,
) {
  this.body = body;
  this.dateTime = dateTime;
  this.id = id;
  this.isViewed = isViewed;
  this.title = title;

  this.setIsViewed = (value) => new this.constructor(body, dateTime, id, value, title);
}

export const MEDICAL_CARD_REJECTED_NOTIFICATION_TYPE = 'medical-card-rejected';

/**
 * @description Уведомление по умолчанию.
 * @param {BaseNotification} base     Уведомление.
 * @param {?string}          imageURL Ссылка на изображение.
 * @param {?string}          videoURL Ссылка на видео.
 * @constructor
 */
export function DefaultNotification(
  base,
  videoURL,
  imageURL,
) {
  this.base = base;
  this.imageURL = imageURL;
  this.videoURL = videoURL;

  this.markAsView = () => new this.constructor(base.setIsViewed(true), videoURL, imageURL);
}

/**
 * Уведомление об отклоненной глав. врачом медицинской карты (визите).
 * @param {BaseNotification} base      Уведомление.
 * @param {number}           patientId Идентификатор пациента.
 * @param {number}           visitId   Идентификатор визита.
 * @constructor
 */
export function MedicalCardRejectedNotification(
  base,
  patientId,
  visitId,
) {
  this.base = base;
  this.patientId = patientId;
  this.visitId = visitId;

  this.markAsView = () => new this.constructor(base.setIsViewed(true), patientId, visitId);
}

/**
 * @description Шлюз уведомлений.
 * @param {Axios} axios HTTP клиент.
 * @constructor
 */
export default function NotificationGateway(axios) {
  /**
   * @description Получить список уведомлений.
   * @param {number} page Номер страницы.
   * @returns {Promise<Maybe>}
   */
  this.viewPage = (page) => axios
    .get('/api/notifications', { params: { page } })
    .then(
      async (response) => {
        const { data } = response.data;
        const items = (data.items || []).map(
          (item) => {
            const notificationType = item.type;
            const base = new BaseNotification(
              item.body,
              item.date_time,
              item.id,
              item.is_viewed,
              item.title,
            );
            if (notificationType === MEDICAL_CARD_REJECTED_NOTIFICATION_TYPE) {
              return new MedicalCardRejectedNotification(
                base,
                item.patient_id,
                item.visit_id,
              );
            }
            return new DefaultNotification(base, item.video_url, item.image_url);
          },
        );
        return Maybe.some({
          items,
          page: data.page,
          pageCount: data.page_count,
        });
      },
      async () => Maybe.none(),
    );

  /**
   * @description Пометить уведомление как посмотренное.
   * @param {number} notificationId Идентификатор уведомления.
   * @returns {Promise<Either>}
   */
  this.markNotificationAsView = (notificationId) => axios.put(`/api/notifications/${notificationId}/view`)
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError) {
          if (error.response && [400, 404].includes(error.response.status)) {
            return Either.left(error.response.data.title);
          }
          if (!error.response) {
            throw new Error(`Network error: сервер по адресу /api/notifications/${notificationId}/view не ответил на запрос`);
          }
        }
        throw error;
      },
    );

  /**
   * @description Получить количество непрочитанных уведомлений.
   * @returns {Promise<number>}
   */
  this.viewNewNotificationCount = () => axios.get('/api/notifications/new-count')
    .then(
      async (response) => Maybe.some(response.data.count || 0),
      async () => Maybe.none(),
    );

  /**
   * @description Пометить уведомление о переносе остатка товара как посмотренное.
   * @returns {Promise<Either>}
   */
  this.markNotificationOfTransferProductBalanceAsViewed = () => axios
    .put('/api/notification-transfer-product-balance')
    .then(
      () => Maybe.some(true),
      () => Maybe.none(),
    );
}
